import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useClickAway } from 'ahooks';
import { PlusOutlined } from '@ant-design/icons';
import { useIntl } from 'umi';

import SvgIcon from '@home/components/SvgIcon';
import modifyConditionIcon from '@/pages/aigc/assets/svg/agent-mutate-filter.svg';
import adjustFormatIcon from '@/pages/aigc/assets/svg/agent-adjust-format.svg';
import agentInstructions from '@/pages/aigc/assets/svg/agent-Instructions.svg';

import excelIcon from '@/pages/aigc/assets/svg/excel.svg';
import aiIcon from '@/pages/aigc/assets/svg/agent-ai-icon.svg';
import {
  parseTemplateToHtml,
  extractTextFromEditor,
  checkIfEdited,
  focusEditableAndMoveCursorToEnd,
  CSS_CLASSES,
} from './utils';
import './index.scss';

/**
 * AI润色快捷选项类型
 */
export enum AIPolishOptionType {
  /** 增加列 */
  ADD_COLUMN = 'add_column',
  /** 增加行 */
  ADD_ROW = 'add_row',
  /** 修改数据条件 */
  MODIFY_CONDITION = 'modify_condition',
  /** 调整数据格式 */
  ADJUST_FORMAT = 'adjust_format',
}

/**
 * AI润色快捷选项配置
 */
export interface IAIPolishOption {
  /** 选项类型 */
  type: AIPolishOptionType;
  /** 显示标签 - i18n key */
  labelKey: string;
  /** 图标类型：'icon' 使用 React 组件，'img' 使用图片 */
  iconType: 'icon' | 'img';
  /** 图标内容 */
  icon: React.ReactNode | string;
  /** 默认模板文案 - i18n key */
  templateKey: string;
}

/**
 * AI润色快捷选项配置列表
 */
export const AI_POLISH_OPTIONS: IAIPolishOption[] = [
  {
    type: AIPolishOptionType.ADD_COLUMN,
    labelKey: 'dba_smart_table_add_column',
    iconType: 'icon',
    icon: <PlusOutlined />,
    templateKey: 'dba_smart_table_add_column_template',
  },
  {
    type: AIPolishOptionType.ADD_ROW,
    labelKey: 'dba_smart_table_add_row',
    iconType: 'icon',
    icon: <PlusOutlined />,
    templateKey: 'dba_smart_table_add_row_template',
  },
  {
    type: AIPolishOptionType.MODIFY_CONDITION,
    labelKey: 'dba_smart_table_modify_data_condition',
    iconType: 'img',
    icon: modifyConditionIcon,
    templateKey: 'dba_smart_table_modify_condition_template',
  },
  {
    type: AIPolishOptionType.ADJUST_FORMAT,
    labelKey: 'dba_smart_table_adjust_data_format',
    iconType: 'img',
    icon: adjustFormatIcon,
    templateKey: 'dba_smart_table_adjust_format_template',
  },
];

interface IAIPolishPopoverProps {
  /** 是否可见 */
  visible: boolean;
  /** 当前表格名称 */
  tableName?: string;
  /** 关闭回调 */
  onClose: () => void;
  /** 发送回调 */
  onSend: (prompt: string) => void;
  /** 是否全屏状态（全屏时需要提升 z-index） */
  isFullScreen?: boolean;
}

/**
 * AI润色当前表格弹层组件
 * 
 * 交互逻辑：
 * 1. 初始状态：显示表格标识 + 输入框 + 快捷选项列表
 * 2. 用户手动输入时：隐藏快捷选项列表
 * 3. 点击快捷选项后：将模板文案填入编辑器，隐藏快捷选项列表
 * 4. 固定文本部分不可编辑，只有【填写】区域可以编辑
 * 5. 未修改任何【填写】区域时：发送按钮禁用
 */
const AIPolishPopover: React.FC<IAIPolishPopoverProps> = ({
  visible,
  tableName = '',
  onClose,
  onSend,
}) => {
  const { formatMessage } = useIntl();
  const defaultTableName = formatMessage({ id: 'dba_smart_table_table' });
  /** 输入框内容（普通输入模式） */
  const [inputValue, setInputValue] = useState<string>('');
  /** 是否显示快捷选项（初始显示，用户输入或选择后隐藏） */
  const [showOptions, setShowOptions] = useState<boolean>(true);
  /** 是否为模板编辑模式 */
  const [isTemplateMode, setIsTemplateMode] = useState<boolean>(false);
  /** 用户是否已修改过【填写】区域 */
  const [hasEdited, setHasEdited] = useState<boolean>(false);
  /** 用于在原生事件回调中读取最新的 hasEdited 状态 */
  const hasEditedRef = useRef<boolean>(false);

  const popoverRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  
  /** 定时器引用，用于清理 */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * 清理定时器
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * 重置所有状态
   */
  const resetState = useCallback(() => {
    clearTimer();
    setInputValue('');
    setShowOptions(true);
    setIsTemplateMode(false);
    setHasEdited(false);
    hasEditedRef.current = false;
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  }, [clearTimer]);

  /**
   * visible 变化时重置状态
   */
  useEffect(() => {
    if (visible) {
      resetState();
      // 聚焦输入框
      timerRef.current = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
    } else {
      // 关闭时也清空状态，避免下次打开时短暂回显
      resetState();
    }
  }, [visible, resetState]);

  /**
   * 组件卸载时清理定时器和事件监听器
   */
  useEffect(() => {
    return () => {
      clearTimer();
      cleanupEditorListeners();
    };
  }, [clearTimer, cleanupEditorListeners]);

  /**
   * 点击外部关闭弹层（使用 useClickAway）
   */
  useClickAway(
    () => {
      if (visible) {
        onClose();
      }
    },
    popoverRef,
    'mousedown'
  );

  /**
   * 处理普通输入变化
   */
  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    // 用户手动输入时隐藏快捷选项
    if (value && showOptions) {
      setShowOptions(false);
    }
  }, [showOptions]);

  /**
   * 处理编辑器内容变化
   * - 只要用户曾经编辑过，就永久放开禁用，不会因改回默认值而重新禁用
   */
  const handleEditorInput = useCallback(() => {
    if (!editorRef.current) return;
    
    setHasEdited((prev) => {
      // 已经编辑过，无需再检查
      if (prev) return true;
      const edited = checkIfEdited(editorRef.current);
      if (edited) {
        hasEditedRef.current = true;
      }
      return edited;
    });
  }, []);

  /**
   * 处理可编辑区域的键盘事件（原生事件）
   * - 阻止回车键换行
   * - Enter 发送，Escape 关闭
   */
  const handleEditableKeyDown = useCallback((e: KeyboardEvent) => {
    // Enter 键：阻止换行，执行发送
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!e.shiftKey) {
        // 需要延迟检查，因为此时状态可能还没更新
        setTimeout(() => {
          if (editorRef.current) {
            // 使用 ref 读取最新的 hasEdited 状态，只要曾经编辑过就允许发送
            const edited = hasEditedRef.current || checkIfEdited(editorRef.current);
            if (edited) {
              const prompt = extractTextFromEditor(editorRef.current);
              onSend(prompt);
              onClose();
            }
          }
        }, 0);
      }
      return;
    }
    
    // Escape 键：关闭弹层
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    
    // 【】里面的内容完全自由编辑，不做任何限制
  }, [onSend, onClose]);

  /**
   * 处理可编辑区域的粘贴事件（原生事件）
   * - 只允许粘贴纯文本，移除换行符
   */
  const handleEditablePaste = useCallback((e: ClipboardEvent) => {
    e.preventDefault();
    
    // 获取纯文本
    const text = e.clipboardData?.getData('text/plain');
    if (!text) return;
    
    // 移除换行符
    const cleanText = text.replace(/[\r\n]/g, ' ');
    
    // 直接插入纯文本
    document.execCommand('insertText', false, cleanText);
  }, []);

  /**
   * 清理编辑器上的事件监听器，防止监听器累积
   */
  const cleanupEditorListeners = useCallback(() => {
    if (!editorRef.current) return;
    const editableWrappers = editorRef.current.querySelectorAll(`.${CSS_CLASSES.EDITABLE_WRAPPER}`);
    editableWrappers.forEach((wrapper) => {
      wrapper.removeEventListener('input', handleEditorInput);
      wrapper.removeEventListener('keydown', handleEditableKeyDown as EventListener);
      wrapper.removeEventListener('paste', handleEditablePaste as EventListener);
    });
  }, [handleEditorInput, handleEditableKeyDown, handleEditablePaste]);

  /**
   * 处理快捷选项点击
   * 点击后：解析模板，进入模板编辑模式
   */
  const handleOptionClick = useCallback((option: IAIPolishOption, e: React.MouseEvent) => {
    // 阻止事件冒泡，防止触发外部点击关闭
    e.stopPropagation();
    
    // 解析模板为HTML
    const template = formatMessage({ id: option.templateKey });
    const html = parseTemplateToHtml(template);
    
    setIsTemplateMode(true);
    setShowOptions(false);
    setHasEdited(false);
    hasEditedRef.current = false;
    
    // 设置编辑器内容（不自动聚焦和选中）
    clearTimer();
    // 先清理旧的事件监听器，防止重复添加导致监听器累积
    cleanupEditorListeners();
    timerRef.current = setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = html;
        
        // 为每个可编辑区域添加事件监听
        const editableWrappers = editorRef.current.querySelectorAll(`.${CSS_CLASSES.EDITABLE_WRAPPER}`);
        editableWrappers.forEach((wrapper) => {
          // input 事件：检测内容变化
          wrapper.addEventListener('input', handleEditorInput);
          // keydown 事件：处理 Enter/Escape
          wrapper.addEventListener('keydown', handleEditableKeyDown as EventListener);
          // paste 事件：只允许纯文本
          wrapper.addEventListener('paste', handleEditablePaste as EventListener);
        });
        
        // 聚焦第一个可编辑区域，并将光标移到文字末尾
        const firstEditable = editableWrappers[0] as HTMLElement;
        if (firstEditable) {
          focusEditableAndMoveCursorToEnd(firstEditable);
        }
      }
    }, 50);
  }, [clearTimer, cleanupEditorListeners, handleEditorInput, handleEditableKeyDown, handleEditablePaste]);

  /**
   * 判断是否可以发送
   * - 普通模式：有内容即可发送
   * - 模板模式：用户必须修改过任何一个【填写】区域
   */
  const canSend = useMemo(() => {
    if (!isTemplateMode) {
      return inputValue.trim().length > 0;
    }
    return hasEdited;
  }, [isTemplateMode, inputValue, hasEdited]);

  /**
   * 组装最终的提示词
   */
  const assemblePrompt = useCallback((): string => {
    if (!isTemplateMode) {
      return inputValue.trim();
    }
    return extractTextFromEditor(editorRef.current);
  }, [isTemplateMode, inputValue]);

  /**
   * 处理发送
   */
  const handleSend = useCallback(() => {
    if (!canSend) return;
    const prompt = assemblePrompt();
    onSend(prompt);
    onClose();
  }, [canSend, assemblePrompt, onSend, onClose]);

  /**
   * 处理键盘事件（普通输入模式）
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) {
        handleSend();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [canSend, handleSend, onClose]);

  /**
   * 渲染选项图标
   */
  const renderOptionIcon = (option: IAIPolishOption) => {
    if (option.iconType === 'icon') {
      return <span className="dba-ai-polish-popover__option-icon">{option.icon}</span>;
    }
    return (
      <img
        src={option.icon as string}
        alt=""
        className="dba-ai-polish-popover__option-icon"
      />
    );
  };

  if (!visible) return null;

  return (
    <div className="dba-ai-polish-popover" ref={popoverRef}>
      {/* 头部区域（表格标识 + 输入框） */}
      <div className="dba-ai-polish-popover__header-section">
        {/* 表格标识 */}
        <div className="dba-ai-polish-popover__header">
          <img src={agentInstructions} alt="" className="dba-ai-polish-popover__instructions-icon" />
          <div className='dba-ai-polish-popover__container'>
            <img src={excelIcon} alt="" className="dba-ai-polish-popover__excel-icon" />
            <span className="dba-ai-polish-popover__table-name">{tableName || defaultTableName}</span>
          </div>
        </div>

        {/* 输入框区域 */}
        <div className="dba-ai-polish-popover__input-wrapper">
          {isTemplateMode ? (
            // 模板编辑模式：外层容器不可编辑，只有【】里面可编辑
            <div
              ref={editorRef}
              className="dba-ai-polish-popover__template-editor"
            />
          ) : (
            // 普通输入模式
            <textarea
              ref={textareaRef}
              rows={1}
              className="dba-ai-polish-popover__input"
              placeholder={formatMessage({ id: 'dba_smart_table_input_placeholder' })}
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          )}
          <button
            className={`dba-ai-polish-popover__send-btn ${!canSend ? 'dba-ai-polish-popover__send-btn--disabled' : ''}`}
            onClick={handleSend}
            disabled={!canSend}
            type="button"
          >
            <SvgIcon type="ai-send" />
          </button>
        </div>
      </div>

      {/* 快捷选项列表 - 选择后或输入后隐藏 */}
      {showOptions && (
        <div className="dba-ai-polish-popover__options">
          <div className="dba-ai-polish-popover__options-header">
            <img src={aiIcon} alt="" className="dba-ai-polish-popover__options-ai-icon" />
            <span className="dba-ai-polish-popover__options-title">{formatMessage({ id: 'dba_smart_table_ai_help_title' })}</span>
          </div>
          <div className="dba-ai-polish-popover__options-list">
            {AI_POLISH_OPTIONS.map((option) => (
              <div
                key={option.type}
                className="dba-ai-polish-popover__option-item"
                role="button"
                tabIndex={0}
                aria-label={formatMessage({ id: option.labelKey })}
                onClick={(e) => handleOptionClick(option, e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOptionClick(option, e as unknown as React.MouseEvent);
                  }
                }}
              >
                {renderOptionIcon(option)}
                <span className="dba-ai-polish-popover__option-label">{formatMessage({ id: option.labelKey })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIPolishPopover;
